// Consent-aware wrapper for HelloRetail tracking calls. AGENTS.md rule #4 puts
// HelloRetail tracking in the same bucket as GA4: consent-gated, mirrored onto
// the dataLayer so a GTM container can trigger on it. Search/product API calls
// are functional, not analytics, and stay on `useNorcePwa()` directly.

import { effectScope, watch } from "vue";
import { createConsentQueue } from "~/layers/base/utils/consentQueue";

type HelloRetailTrackArg = Parameters<
	ReturnType<typeof useNorcePwa>["helloRetailApi"]["track"]
>[0];

// The consent store reads its cookie on `app:mounted` (cookieConsent.client.ts),
// which runs after components' `onMounted`. A pageview fired on a hard load
// would see "undecided" even for a visitor who consented long ago, so calls
// made before the decision is known wait here instead of being dropped.
const pendingTracks = createConsentQueue<HelloRetailTrackArg>();
let isFlushScheduled = false;

export const useHelloRetailTracking = () => {
	const { helloRetailApi } = useNorcePwa();
	const { categories, hasDecided } = useCookieConsent();
	const {
		public: {
			norce: { isHelloRetailTracking }
		}
	} = useRuntimeConfig();

	// The cookie consent store exposes `statistics`, `marketing`, and
	// `preferences` (plus the always-true `necessary`). HelloRetail behaviour
	// tracking falls under statistics. If a future requirement puts it under
	// marketing instead, change the lookup here.
	const isStatisticsGranted = () => categories.value?.statistics === true;

	const send = (payload: HelloRetailTrackArg) => {
		// What a GTM container triggers on; trigger config lives in GTM.
		const w = window as unknown as { dataLayer?: unknown[] };
		w.dataLayer = w.dataLayer || [];
		w.dataLayer.push({
			event: "helloretail_track",
			helloretail: payload
		});

		try {
			helloRetailApi.track(payload);
		} catch (err) {
			console.warn(
				"[useHelloRetailTracking] direct track failed:",
				err instanceof Error ? err.message : err
			);
		}
	};

	// Detached scope with its own consent refs: the caller (and the computeds
	// it created) may be unmounted before the visitor decides.
	const scheduleFlush = () => {
		if (isFlushScheduled) return;
		isFlushScheduled = true;
		const scope = effectScope(true);
		scope.run(() => {
			const consent = useCookieConsent();
			watch(consent.hasDecided, (decided) => {
				if (!decided) return;
				scope.stop();
				isFlushScheduled = false;
				const isGranted = consent.categories.value?.statistics === true;
				pendingTracks.flush(isGranted ? send : undefined);
			});
		});
	};

	const helloRetailTrack = (payload: HelloRetailTrackArg) => {
		// Never on the server.
		if (!isHelloRetailTracking || import.meta.server) return;

		if (!hasDecided.value) {
			pendingTracks.push(payload);
			scheduleFlush();
			return;
		}

		if (isStatisticsGranted()) send(payload);
	};

	return {
		helloRetailTrack
	};
};
